import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { useIntl } from "react-intl";
import { Button } from "primereact/button";
import TooltipButton from "../../../common/tooltipButton";
import { Dialog } from "primereact/dialog";
import { Message } from "primereact/message";
import { ProgressBar } from "primereact/progressbar";
import FileUpload from "../../../common/fileUpload";
import * as UserUploadConstants from "../../../../constants/UserUploadConstants";
import {
  errorMessageId,
  isFinalStatus,
  resultsFileName,
  statusLabelId,
} from "../../../../helpers/UserUploadHelper";
import { downloadTemplateRequest } from "../../../../store/slices/usersSlice";
import {
  userUploadDownloadRequest,
  userUploadPollRequest,
  userUploadRequest,
  userUploadReset,
} from "../../../../store/slices/userUploadSlice";
import UserUploadResult from "./UserUploadResult";

// "Bulk import" on the Users list: pick the employee file, upload it, then
// poll GET api/users/uploads/{id} every 3 seconds (without the rows) until the
// status is final, fetch the rows that need attention once and show the result.
export const UserUploadDialog = ({
  visible,
  canUpload = false,
  upload = null,
  action = null,
  requesting = false,
  error = null,
  onHide,
  onUpload,
  onPoll,
  onReset,
  onDownloadTemplate,
  onDownloadResults,
  onFinished,
  onOpenHistory,
}) => {
  const intl = useIntl();
  const message = (id) => intl.formatMessage({ id });
  const [file, setFile] = useState(null);
  // The store is persisted, so an upload in it is only shown once this
  // dialog has sent one.
  const [submitted, setSubmitted] = useState(false);
  const finishedIdRef = useRef(null);
  const rowsRequestedIdRef = useRef(null);
  const callbacksRef = useRef({});
  callbacksRef.current = { onPoll, onReset, onFinished };

  const current = submitted ? upload : null;
  const uploadError =
    submitted && !current && action === "upload" && error ? error : null;
  const pollError = current && action === "poll" && error ? error : null;
  const isFinal = Boolean(current) && isFinalStatus(current.status);

  useEffect(() => {
    if (visible) {
      callbacksRef.current.onReset();
      setFile(null);
      setSubmitted(false);
      finishedIdRef.current = null;
      rowsRequestedIdRef.current = null;
    }
  }, [visible]);

  useEffect(() => {
    if (uploadError) {
      setFile(null);
    }
  }, [uploadError]);

  // Every poll answer is a new object, so this schedules the next poll.
  useEffect(() => {
    if (!visible || !current || isFinalStatus(current.status) || pollError) {
      return undefined;
    }
    const timer = setTimeout(
      () => callbacksRef.current.onPoll({ id: current.id }),
      UserUploadConstants.USER_UPLOAD_POLL_INTERVAL_MS
    );
    return () => clearTimeout(timer);
  }, [visible, current, pollError]);

  // Polls leave the rows out: ask for them once the status is final.
  useEffect(() => {
    if (
      visible &&
      isFinal &&
      current.rows == null &&
      rowsRequestedIdRef.current !== current.id
    ) {
      rowsRequestedIdRef.current = current.id;
      callbacksRef.current.onPoll({ id: current.id, includeRows: true });
    }
  }, [visible, isFinal, current]);

  useEffect(() => {
    if (isFinal && finishedIdRef.current !== current.id) {
      finishedIdRef.current = current.id;
      if (callbacksRef.current.onFinished) {
        callbacksRef.current.onFinished(current);
      }
    }
  }, [isFinal, current]);

  const handleUpload = () => {
    setSubmitted(true);
    onUpload({ file });
  };

  const renderSelect = () => (
    <div className="flex flex-column gap-3">
      <p className="m-0">{message("user_upload_template_hint")}</p>
      <div>
        <Button
          type="button"
          link
          className="p-0"
          icon="pi pi-cloud-download"
          label={message("user_upload_download_template")}
          onClick={onDownloadTemplate}
        />
      </div>
      <FileUpload
        shouldManualUpload={false}
        name="user_upload"
        label="user_upload_file_label"
        inputLabel={message("user_upload_file_label")}
        fileType={UserUploadConstants.USER_UPLOAD_FILE_TYPE}
        maxFileSize={UserUploadConstants.USER_UPLOAD_MAX_FILE_BYTES}
        onUpload={(event) => setFile(event.files?.[0] || null)}
        onClear={() => setFile(null)}
        error={uploadError}
      />
      <Message severity="warn" text={message("user_upload_audience_note")} />
      {(uploadError && (
        <div className="flex flex-column gap-1">
          <Message
            severity="error"
            text={message(errorMessageId(uploadError.code))}
          />
          {(uploadError.message &&
            UserUploadConstants.USER_UPLOAD_COLUMN_ERRORS.includes(
              uploadError.code
            ) && <small>{uploadError.message}</small>) ||
            null}
        </div>
      )) ||
        null}
    </div>
  );

  const renderProgress = () => (
    <div className="flex flex-column gap-3">
      <div className="flex flex-wrap align-items-center gap-2">
        <strong>{current.fileName}</strong>
        <span>{message(statusLabelId(current.status))}</span>
      </div>
      <ProgressBar mode="indeterminate" style={{ height: "6px" }} />
      <p className="m-0">{message("user_upload_processing")}</p>
      {(pollError && (
        <Message severity="error" text={message("user_upload_poll_failed")} />
      )) ||
        null}
    </div>
  );

  const renderBody = () => {
    if (!current) {
      return renderSelect();
    }
    if (!isFinal) {
      return renderProgress();
    }
    return (
      <div className="flex flex-column gap-3">
        <UserUploadResult
          upload={current}
          onDownloadResults={onDownloadResults}
        />
        {(pollError && (
          <Message severity="error" text={message("user_upload_poll_failed")} />
        )) ||
          null}
      </div>
    );
  };

  const footer = current ? (
    <div className="flex justify-content-end gap-2">
      {(onOpenHistory && (
        <Button
          type="button"
          link
          label={message("user_upload_view_history")}
          onClick={onOpenHistory}
        />
      )) ||
        null}
      <Button
        type="button"
        label={message("user_upload_close")}
        onClick={onHide}
      />
    </div>
  ) : (
    <div className="flex justify-content-end gap-2">
      <Button
        type="button"
        outlined
        label={message("cancel")}
        onClick={onHide}
      />
      <TooltipButton
        type="button"
        icon="pi pi-cloud-upload"
        label={message("user_upload_submit")}
        loading={requesting && action === "upload"}
        disabled={!canUpload || !file}
        tooltip={canUpload ? "" : message("no_permission")}
        tooltipOptions={{ showOnDisabled: true, position: "bottom" }}
        onClick={handleUpload}
      />
    </div>
  );

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      header={message("user_upload_title")}
      footer={footer}
      style={{ width: "50rem" }}
      breakpoints={{ "960px": "95vw" }}
    >
      {(visible && renderBody()) || null}
    </Dialog>
  );
};

UserUploadDialog.propTypes = {
  visible: PropTypes.bool.isRequired,
  canUpload: PropTypes.bool,
  upload: PropTypes.object,
  action: PropTypes.string,
  requesting: PropTypes.bool,
  error: PropTypes.object,
  onHide: PropTypes.func.isRequired,
  onUpload: PropTypes.func.isRequired,
  onPoll: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  onDownloadTemplate: PropTypes.func.isRequired,
  onDownloadResults: PropTypes.func.isRequired,
  onFinished: PropTypes.func,
  onOpenHistory: PropTypes.func,
};

const mapStateToProps = (state) => ({
  upload: state.userUploads.current,
  action: state.userUploads.action,
  requesting: state.userUploads.requesting,
  error: state.userUploads.error,
});

const mapDispatchToProps = (dispatch) => ({
  onUpload: (data) => dispatch(userUploadRequest(data)),
  onPoll: (data) => dispatch(userUploadPollRequest(data)),
  onReset: () => dispatch(userUploadReset()),
  onDownloadTemplate: () => dispatch(downloadTemplateRequest()),
  onDownloadResults: (upload) =>
    dispatch(
      userUploadDownloadRequest({
        id: upload.id,
        kind: UserUploadConstants.USER_UPLOAD_FILE_RESULTS,
        fileName: resultsFileName(upload.fileName),
      })
    ),
});

export default connect(mapStateToProps, mapDispatchToProps)(UserUploadDialog);
